import React from "react";

const statusConfig = {
    HEALTHY: {
        label: "Connected",
        bgColor: "bg-accent-50",
        textColor: "text-accent-600",
        icon: (
            <svg className="w-3 h-3" viewBox="0 0 12 12" fill="none">
                <path d="M10 3L4.5 8.5L2 6" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
        )
    },
    NEEDS_REAUTH: {
        label: "Needs Reconnection",
        bgColor: "bg-amber-50",
        textColor: "text-amber-600",
        icon: (
            <svg className="w-3 h-3" viewBox="0 0 12 12" fill="none">
                <path d="M6 1V7M6 9V11" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round"/>
            </svg>
        )
    },
    ERROR: {
        label: "Connection Error",
        bgColor: "bg-red-50",
        textColor: "text-danger-500",
        icon: (
            <svg className="w-3 h-3" viewBox="0 0 12 12" fill="none">
                <path d="M6 4V6M6 8H6.005M1 6C1 8.76142 3.23858 11 6 11C8.76142 11 11 8.76142 11 6C11 3.23858 8.76142 1 6 1C3.23858 1 1 3.23858 1 6Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
        )
    },
    SYNCING: {
        label: "Syncing...",
        bgColor: "bg-accent-50",
        textColor: "text-accent-600",
        icon: (
            <svg className="w-3 h-3 animate-spin" viewBox="0 0 12 12" fill="none">
                <path d="M6 1V3M6 9V11M1 6H3M9 6H11M2.05 2.05L3.47 3.47M8.53 8.53L9.95 9.95M2.05 9.95L3.47 8.53M8.53 3.47L9.95 2.05" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
        )
    },
    UNKNOWN: {
        label: "Checking...",
        bgColor: "bg-gray-100",
        textColor: "text-gray-500",
        icon: (
            <svg className="w-3 h-3" viewBox="0 0 12 12" fill="none">
                <path d="M4.5 4.5C4.5 3.67157 5.17157 3 6 3C6.82843 3 7.5 3.67157 7.5 4.5C7.5 5.32843 6.82843 6 6 6V7M6 9H6.005" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
        )
    }
};

export default function ConnectionStatusBadge({ status, showLabel = true, size = "sm" }) {
    const config = statusConfig[status] || statusConfig.UNKNOWN;

    const sizeClasses = {
        xs: "px-1.5 py-0.5 text-xs gap-1",
        sm: "px-2 py-0.5 text-xs gap-1.5",
        md: "px-2.5 py-1 text-sm gap-1.5"
    };

    return (
        <span className={`inline-flex items-center rounded ${config.bgColor} ${config.textColor} ${sizeClasses[size]}`}>
            {config.icon}
            {showLabel && <span>{config.label}</span>}
        </span>
    );
}

export function ConnectionHealthAlert({ links }) {
    const needsAttention = links.filter(l => l.needsAttention);

    if (needsAttention.length === 0) return null;

    return (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg mb-4">
            <div className="flex items-start gap-3">
                <div className="text-amber-600 mt-0.5">
                    <svg className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                </div>
                <div className="flex-1">
                    <h3 className="text-amber-600 font-medium">
                        {needsAttention.length === 1
                            ? "1 connection needs attention"
                            : `${needsAttention.length} connections need attention`}
                    </h3>
                    <ul className="mt-2 space-y-1">
                        {needsAttention.map(link => (
                            <li key={link.id} className="text-sm text-amber-700">
                                <span className="font-medium">
                                    {link.institutionDisplayName || `Account #${link.id}`}
                                </span>
                                {link.lastErrorMessage && (
                                    <span className="text-amber-600/80"> - {link.lastErrorMessage.substring(0, 100)}</span>
                                )}
                            </li>
                        ))}
                    </ul>
                    <p className="mt-2 text-sm text-amber-600/80">
                        Click "Reconnect" to fix the issue.
                    </p>
                </div>
            </div>
        </div>
    );
}
